/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Optional } from '../@types/common';

/**
 * Represents a simple Matrix namespaced value. This will assume that if a stable prefix
 * is provided that the stable prefix should be used when representing the identifier.
 */
export class NamespacedValue<S extends string, U extends string> {
  // Stable is optional, but one of the two parameters is required, hence the weird-looking types.
  // Goal is to to have developers explicitly say there is no stable value (if applicable).
  public constructor(stable: S, unstable: U);
  public constructor(stable: S, unstable?: U);
  public constructor(stable: null | undefined, unstable: U);
  public constructor(
    public readonly stable?: S | null,
    public readonly unstable?: U,
  ) {
    if (!this.unstable && !this.stable) {
      throw new Error('One of stable or unstable values must be supplied');
    }
  }

  public get name(): U | S {
    if (this.stable) {
      return this.stable;
    }
    return this.unstable!;
  }

  public get altName(): U | S | null | undefined {
    if (!this.stable) {
      return null;
    }
    return this.unstable;
  }

  public get names(): (U | S)[] {
    const names = [this.name];
    const altName = this.altName;
    if (altName) names.push(altName);
    return names;
  }

  public matches(val: string): boolean {
    return this.name === val || this.altName === val;
  }

  // this desperately wants https://github.com/microsoft/TypeScript/pull/26349 at the top level of the class
  // so we can instantiate `NamespacedValue<string, _, _>` as a default type for that namespace.
  public findIn<T>(obj: any): Optional<T> {
    let val: T | undefined;
    if (this.name) {
      val = obj?.[this.name];
    }
    if (!val && this.altName) {
      val = obj?.[this.altName];
    }
    return val;
  }

  public includedIn(arr: any[]): boolean {
    let included = false;
    if (this.name) {
      included = arr.includes(this.name);
    }
    if (!included && this.altName) {
      included = arr.includes(this.altName);
    }
    return included;
  }
}

export class ServerControlledNamespacedValue<
  S extends string,
  U extends string,
> extends NamespacedValue<S, U> {
  private preferUnstable = false;

  public setPreferUnstable(preferUnstable: boolean): void {
    this.preferUnstable = preferUnstable;
  }

  public get name(): U | S {
    if (this.stable && !this.preferUnstable) {
      return this.stable;
    }
    return this.unstable!;
  }
}

/**
 * Represents a namespaced value which prioritizes the unstable value over the stable
 * value.
 */
export class UnstableValue<
  S extends string,
  U extends string,
> extends NamespacedValue<S, U> {
  // Note: Constructor difference is that `unstable` is *required*.
  public constructor(stable: S, unstable: U) {
    super(stable, unstable);
    if (!this.unstable) {
      throw new Error('Unstable value must be supplied');
    }
  }

  public get name(): U {
    return this.unstable!;
  }

  public get altName(): S {
    return this.stable!;
  }
}
